import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CircleHelp, CreditCard, FileText, FlaskConical, GitMerge, KeyRound, Mail, Receipt, StickyNote, Tag, Workflow } from "lucide-react";
import type { Contact } from "@/lib/types";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label, Textarea } from "@/components/ui/input";
import { OPEN_TAG_PICKER, useAddNote, useMerge } from "./hooks";
import { LoginLinkDialog, SendEmailDialog, StartAutomationDialog } from "./ActionDialogs";
import { InvoiceDialog, TakePaymentDialog } from "../billing/BillingDialogs";
import { SignInExplainDialog } from "./SignInExplainDialog";

/** Action bar: note, email, tag, automation, payment/order, invoice/pay link, login link, "why can't they sign in?", merge. */
export function ActionBar({ c }: { c: Contact }) {
  const [dialog, setDialog] = useState<"note" | "merge" | "email" | "automation" | "login" | "explain" | "payment" | "invoice" | "test" | null>(null);
  const archivedTip = c.archived ? "Archived contacts are reference only" : undefined;
  const qc = useQueryClient();
  // Billing's dialogs refresh the timeline; the header (lifetime value, orders) and billing panels are refreshed here.
  const closeBilling = () => {
    setDialog(null);
    for (const key of ["contact", "billing-summary", "billing-detail"]) void qc.invalidateQueries({ queryKey: [key, c.id] });
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      <Button variant="outline" size="sm" onClick={() => setDialog("note")}>
        <StickyNote /> Note
      </Button>
      <Button variant="outline" size="sm" onClick={() => setDialog("email")} disabled={c.archived || c.emails.length === 0} title={c.archived ? "Archived contacts are never emailed" : undefined}>
        <Mail /> Email
      </Button>
      <Button variant="outline" size="sm" onClick={() => window.dispatchEvent(new Event(OPEN_TAG_PICKER))}>
        <Tag /> Tag
      </Button>
      <Button variant="outline" size="sm" onClick={() => setDialog("automation")} disabled={c.archived}>
        <Workflow /> Start automation
      </Button>
      <Button variant="outline" size="sm" onClick={() => setDialog("payment")} disabled={c.archived} title={archivedTip}>
        <CreditCard /> Take payment / new order
      </Button>
      <Button variant="outline" size="sm" onClick={() => setDialog("invoice")} disabled={c.archived} title={archivedTip}>
        <Receipt /> Invoice / pay link
      </Button>
      <Button variant="outline" size="sm" onClick={() => setDialog("login")} disabled={c.archived}>
        <KeyRound /> Send login link
      </Button>
      <Button variant="outline" size="sm" onClick={() => setDialog("explain")}>
        <CircleHelp /> Why can't they sign in?
      </Button>
      <Button variant="outline" size="sm" onClick={() => setDialog("merge")}>
        <GitMerge /> Merge
      </Button>
      <Button variant="outline" size="sm" onClick={() => setDialog("test")} title="Test contacts are left out of sales and email report numbers">
        <FlaskConical /> {c.is_test ? "Test contact" : "Mark as test"}
      </Button>
      <NoteDialog c={c} open={dialog === "note"} onClose={() => setDialog(null)} />
      <MergeDialog c={c} open={dialog === "merge"} onClose={() => setDialog(null)} />
      <TestContactDialog c={c} open={dialog === "test"} onClose={() => setDialog(null)} />
      {dialog === "email" && <SendEmailDialog c={c} onClose={() => setDialog(null)} />}
      {dialog === "automation" && <StartAutomationDialog c={c} onClose={() => setDialog(null)} />}
      {dialog === "login" && <LoginLinkDialog c={c} onClose={() => setDialog(null)} />}
      {dialog === "explain" && <SignInExplainDialog contactId={c.id} onClose={() => setDialog(null)} />}
      {dialog === "payment" && <TakePaymentDialog contactId={c.id} onClose={closeBilling} />}
      {dialog === "invoice" && <InvoiceDialog contactId={c.id} defaultBrandId={c.brands[0]?.brand_id} onClose={closeBilling} />}
    </div>
  );
}

/** Test contact on/off (Brandon, 2026-11-04): tests aren't tracked like real sends or sales. */
export function TestContactDialog({ c, open, onClose }: { c: Contact; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const set = useMutation({
    mutationFn: (isTest: boolean) => api<{ is_test: boolean }>(`/api/v1/contacts/${c.id}/test`, { json: { is_test: isTest } }),
    onSuccess: () => {
      for (const key of ["contact", "timeline"]) void qc.invalidateQueries({ queryKey: [key, c.id] });
      onClose();
    },
  });
  const marking = !c.is_test;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>{marking ? "Mark as a test contact?" : "Count this contact again?"}</DialogTitle>
        <DialogDescription>
          {marking
            ? "A test contact still gets every email and can still buy, but is left out of the numbers: home page stats, saved reports (sales, payments, money due, new contacts) and email report totals. Use it for test addresses, not real customers. For a real person's test purchase, mark the order as a test instead."
            : "This is a test contact now. Making it a real contact again puts its purchases and emails back in the numbers."}
        </DialogDescription>
        {set.error && <p className="text-sm text-[var(--color-destructive)]">{(set.error as ApiError).message}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => set.mutate(marking)} disabled={set.isPending}>
            {marking ? "Mark as test" : "Make it a real contact"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NoteDialog({ c, open, onClose }: { c: Contact; open: boolean; onClose: () => void }) {
  const [body, setBody] = useState("");
  const [kind, setKind] = useState("note");
  const [pinned, setPinned] = useState(false);
  const add = useAddNote(c.id);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>
          <FileText className="mr-1 inline size-4" /> Add a note
        </DialogTitle>
        <DialogDescription>It goes on the timeline. Notes are visible to everyone who can open this contact.</DialogDescription>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate({ body, kind, pinned }, { onSuccess: () => (setBody(""), setPinned(false), onClose()) });
          }}
        >
          <div className="flex gap-1" role="radiogroup" aria-label="What kind of note">
            {[
              ["note", "Note"],
              ["call", "Phone call"],
              ["meeting", "Meeting"],
              ["email", "Email"],
            ].map(([k, label]) => (
              <Button key={k} type="button" size="sm" role="radio" aria-checked={kind === k} variant={kind === k ? "default" : "outline"} onClick={() => setKind(k)}>
                {label}
              </Button>
            ))}
          </div>
          <Textarea autoFocus aria-label="Note text" value={body} onChange={(e) => setBody(e.target.value)} placeholder="What happened?" required />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} className="accent-[var(--color-primary)]" />
            Pin it to the top of the contact
          </label>
          {add.error && <p className="text-sm text-[var(--color-destructive)]">{(add.error as ApiError).message}</p>}
          <div className="flex justify-end">
            <Button type="submit" disabled={add.isPending || !body.trim()}>
              Save note
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MergeDialog({ c, open, onClose }: { c: Contact; open: boolean; onClose: () => void }) {
  const [loser, setLoser] = useState("");
  const [reason, setReason] = useState("");
  const merge = useMerge(c.id);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>Merge a duplicate into #{c.id}</DialogTitle>
        <DialogDescription>
          Both records are saved in a note first. This contact's values win; the duplicate's emails, tags, orders and history move here, and its ID keeps
          working.
        </DialogDescription>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            merge.mutate({ loserId: Number(loser), reason }, { onSuccess: () => (setLoser(""), setReason(""), onClose()) });
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="loser">Duplicate contact ID</Label>
            <Input id="loser" inputMode="numeric" value={loser} onChange={(e) => setLoser(e.target.value.replace(/\D/g, ""))} required />
          </div>
          <div className="space-y-1">
            <Label htmlFor="reason">Why they're the same person</Label>
            <Input id="reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Same email" />
          </div>
          {merge.error && <p className="text-sm text-[var(--color-destructive)]">{(merge.error as ApiError).message}</p>}
          <div className="flex justify-end">
            <Button type="submit" variant="destructive" disabled={merge.isPending || !loser || Number(loser) === c.id}>
              Merge #{loser || "…"} into #{c.id}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
